import { useEffect, useState, useMemo, Dispatch } from 'react'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { Accordion, Icon, Text, Card, CardColors, Link } from '@/ui'
import { TypographyStyles } from '@/styles'
import { UploadDocumentType } from '@/constants/businessConstants'
import { getMissingStatements } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { MissingStatements } from '../missing-bank-statements'
import {
  fetchFinancialAccountsIfNeeded,
  StatementStatusType,
} from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { selectUserStatements } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { selectPlaidItems } from '@/selectors/financeSelectors'
import { NotExpectedState, NotExpectedStateAction } from './types'

// TODO: migrate to v2
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'

export const NoLBAIntroPage = ({
  setShowSkipButton,
  notExpectedState,
  setNotExpectedValue,
}: {
  setShowSkipButton: (showSkipButton: boolean) => void
  notExpectedState: NotExpectedState
  setNotExpectedValue: Dispatch<NotExpectedStateAction>
}) => {
  const dispatch = useAppDispatch()
  setShowSkipButton(false)
  const [uploadFileModalOpen, setUploadFileModalOpen] = useState(false)
  const currentYear = new Date().getFullYear()
  const missingStatements = useFetchResponse(getMissingStatements)
  const statements = useReselector(selectUserStatements)
  const plaidItems = useReselector(selectPlaidItems)
  const plaidStatementAccounts = useMemo(() => {
    return Object.values(plaidItems).filter((item) => {
      return (
        item.accounts[0].statementPermissions ===
        StatementStatusType.PLAID_STATEMENT
      )
    })
  }, [plaidItems])

  const financialStatements = useMemo(() => {
    if (missingStatements?.length) {
      const groupedStatements = missingStatements
        .filter((statement) => statement.financial_institution_id !== undefined)
        .reduce(
          (acc, statement) => {
            const { financial_institution_id } = statement

            if (financial_institution_id && !acc[financial_institution_id]) {
              acc[financial_institution_id] = []
            }

            if (financial_institution_id) {
              acc[financial_institution_id].push(statement)
            }

            return acc
          },
          {} as Record<string, typeof missingStatements>
        )
      for (const statement of statements) {
        const financialAccountId =
          statement.statement?.statementFinancialAccounts?.[0]
            ?.financialAccountId
        if (!financialAccountId) continue

        for (const group of Object.values(groupedStatements)) {
          const match = group.find(
            (account) => account.financialAccountId === financialAccountId
          )
          if (match) {
            if (
              match.financial_institution_id &&
              groupedStatements[match.financial_institution_id]
            ) {
              for (const statementGrouped of groupedStatements[
                match.financial_institution_id
              ]) {
                const isoDate = statement.statement?.statementMonth
                  ? DateTime.fromFormat(
                      statement.statement.statementMonth,
                      DATE_FORMATS_LUXON.YEAR_MONTH
                    )
                      .startOf('month')
                      .toISO()
                  : null
                if (
                  isoDate &&
                  !statementGrouped.statements.includes(isoDate) &&
                  financialAccountId === statementGrouped.financialAccountId
                ) {
                  if (statement.statement?.statementMonth) {
                    statementGrouped.statements.push(isoDate)
                  }
                }
              }
            }
            break // Exit the inner loop but continue processing other statements
          }
        }
      }
      return Object.values(groupedStatements)
    }
    return []
  }, [missingStatements, statements])

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchFinancialAccountsIfNeeded())
      await dispatch(fetchPlaidItemsIfNeeded())
    }
    setShowSkipButton(true)
    fetch()
  }, [dispatch, setShowSkipButton])

  return (
    <div className="space-y-6">
      <Text variant={TypographyStyles.H1} className="text-forest">
        Upload monthly bank statements
      </Text>

      <Text>
        Finish setting up your account by uploading the monthly bank statements
        below. This is important for compliance and completeness of your books.
      </Text>

      <Text variant={TypographyStyles.H3}>Instructions</Text>
      <div className="space-y-1">
        <Text>1. Log into each connected business account.</Text>
        <Text>
          2. Download all monthly statements for each month of {currentYear}.
        </Text>
        <Text>3. Upload these statements (JPG, PNG, PDF) here.</Text>
      </div>

      <Card color={CardColors.EMPHASIS}>
        <div className="flex flex-row items-center">
          <Icon icon={faCircleInfo} className="mr-1" size="1x" />
          <Text variant={TypographyStyles.BODY_SM}>
            Our bookkeeping team will use your statements to set up
            auto-reconciliation. This way, you won’t have to upload more except
            in case of an error.
            <Link
              className="ml-1"
              href="https://support.joinheard.com/hc/en-us/articles/27502757159703-What-is-Auto-reconciliation"
              newPage
            >
              Learn More -&gt;
            </Link>
          </Text>
        </div>
      </Card>

      <Accordion type="multiple" variant="text">
        <Accordion.Item value="why-statements">
          <Accordion.Trigger>
            <Text variant={TypographyStyles.BODY_MD} className="font-semibold">
              Why do you need my bank statements?
            </Text>
          </Accordion.Trigger>
          <Accordion.Content>
            <Text>
              These help us complete your financial record from before you
              joined Heard. This is important for bookkeeping accuracy and
              compliance. This also allows us to:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Provide financial insights and recommendations</li>
                <li>Calculate accurate quarterly tax estimates</li>
                <li>
                  Check that all income and expenses are properly accounted for
                </li>
              </ul>
            </Text>
          </Accordion.Content>
        </Accordion.Item>

        <Accordion.Item value="missing-statement">
          <Accordion.Trigger>
            <Text variant={TypographyStyles.BODY_MD} className="font-semibold">
              What if I can&apos;t find a statement right now?
            </Text>
          </Accordion.Trigger>
          <Accordion.Content>
            <Text>
              We <b>strongly recommend</b> taking the time to upload all
              required statements. They are needed to fully set up your account,
              as well as for compliance, tax, and bookkeeping purposes.
            </Text>
          </Accordion.Content>
        </Accordion.Item>

        <Accordion.Item value="multi-month-statement">
          <Accordion.Trigger>
            <Text variant={TypographyStyles.BODY_MD} className="font-semibold">
              What if I have a statement that covers multiple accounts or
              months?
            </Text>
          </Accordion.Trigger>
          <Accordion.Content>
            <Text>
              <ol className="list-decimal pl-5 space-y-1">
                <li>
                  Select{' '}
                  <b>
                    “I have a statement covering multiple months or accounts”
                  </b>
                  .
                </li>
                <li>
                  Upload the statement to the statements card that appears.
                  It&apos;s okay to upload the same statement for each covered
                  account.
                </li>
              </ol>
            </Text>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
      <div className="space-y-4">
        {financialStatements?.map((statement) => (
          <MissingStatements
            key={statement[0].financial_institution_id}
            missingStatements={statement}
            statements={statements}
            notExpectedState={notExpectedState}
            setNotExpectedValue={setNotExpectedValue}
          />
        ))}

        {plaidStatementAccounts?.map((account) => (
          <MissingStatements
            key={account.institutionId}
            missingStatements={[]}
            statements={statements}
            plaidAccount={account}
            isPlaid
          />
        ))}
      </div>

      <FileUploadModal
        documentType={UploadDocumentType.STATEMENT}
        open={uploadFileModalOpen}
        close={() => setUploadFileModalOpen(false)}
        userFacing
        useFilteredAccountOptions
      />
    </div>
  )
}
